import { Theme } from "@react-navigation/native";
import { Platform } from "react-native";
import { configureFonts, MD3Theme } from "react-native-paper";

// building MD3 tokens from colors
// https://m3.material.io/theme-builder#/custom

// input colors:
// primary: #1ba39c | teal-500
// secondary: ##52489C | purple-500
// tertiary: #E9C46A | hansa yellow (plato)
// neutral: #646566 | gray-500

type Font = {
  fontFamily: string;
  fontWeight?:
    | "normal"
    | "bold"
    | "100"
    | "200"
    | "300"
    | "400"
    | "500"
    | "600"
    | "700"
    | "800"
    | "900";
};

const tokens = {
  palette: {
    /* primary */
    primary0: "#000000",
    primary10: "#00201e",
    primary20: "#003734",
    primary25: "#004340",
    primary30: "#00504c",
    primary35: "#005d58",
    primary40: "#006a65",
    primary50: "#00857f",
    primary60: "#00a29b",
    primary70: "#24beb6",
    primary80: "#4fdad2",
    primary90: "#70f7ee",
    primary95: "#b1fff8",
    primary98: "#e3fffb",
    primary99: "#f2fffd",
    primary100: "#ffffff",
    /* secondary */
    secondary0: "#000000",
    secondary10: "#180262",
    secondary20: "#2e2176",
    secondary25: "#392e82",
    secondary30: "#453a8e",
    secondary35: "#50469a",
    secondary40: "#5d53a7",
    secondary50: "#766cc2",
    secondary60: "#9086de",
    secondary70: "#aaa0fb",
    secondary80: "#c7bfff",
    secondary90: "#e4dfff",
    secondary95: "#f3eeff",
    secondary98: "#fcf8ff",
    secondary99: "#fffbff",
    secondary100: "#ffffff",
    /* tertiary */
    tertiary0: "#000000",
    tertiary10: "#251a00",
    tertiary20: "#3e2e00",
    tertiary25: "#4c3900",
    tertiary30: "#5a4400",
    tertiary35: "#684f00",
    tertiary40: "#765a00",
    tertiary50: "#957200",
    tertiary60: "#b38b0e",
    tertiary70: "#d1a52e",
    tertiary80: "#eec148",
    tertiary90: "#ffdf96",
    tertiary95: "#ffefd1",
    tertiary98: "#fff8f1",
    tertiary99: "#fffbff",
    tertiary100: "#ffffff",
    /* neutral */
    neutral0: "#000000",
    neutral10: "#001f29",
    neutral20: "#003545",
    neutral25: "#004153",
    neutral30: "#004d62",
    neutral35: "#005a72",
    neutral40: "#006782",
    neutral50: "#0081a3",
    neutral60: "#009dc5",
    neutral70: "#3cb8e1",
    neutral80: "#60d4fe",
    neutral90: "#baeaff",
    neutral95: "#dff4ff",
    neutral98: "#f3faff",
    neutral99: "#fafcff",
    neutral100: "#ffffff",
    /* neutral variant */
    neutralVariant0: "#000000",
    neutralVariant10: "#141d1c",
    neutralVariant20: "#293231",
    neutralVariant25: "#343d3c",
    neutralVariant30: "#3f4947",
    neutralVariant35: "#4a5453",
    neutralVariant40: "#56605f",
    neutralVariant50: "#6f7978",
    neutralVariant60: "#889391",
    neutralVariant70: "#a3adac",
    neutralVariant80: "#bec9c7",
    neutralVariant90: "#dae5e3",
    neutralVariant95: "#e9f3f1",
    neutralVariant98: "#f1fcf9",
    neutralVariant99: "#f4fefc",
    neutralVariant100: "#ffffff",
    /* error */
    error0: "#000000",
    error10: "#410002",
    error20: "#690005",
    error25: "#7e0007",
    error30: "#93000a",
    error35: "#a80710",
    error40: "#ba1a1a",
    error50: "#de3730",
    error60: "#ff5449",
    error70: "#ff897d",
    error80: "#ffb4ab",
    error90: "#ffdad6",
    error95: "#ffedea",
    error98: "#fff8f7",
    error99: "#fffbff",
    error100: "#ffffff",
  },

  opacity: {
    level1: 0.08,
    level2: 0.12,
    level3: 0.16,
    level4: 0.38,
  },

  // TODO: figure out how it interops back with font config
  typeface: {
    brandRegular: Platform.select({
      web: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
      ios: "System",
      default: "sans-serif",
    }),
    weightRegular: "400" as Font["fontWeight"],

    plainMedium: Platform.select({
      web: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
      ios: "System",
      default: "sans-serif-medium",
    }),
    weightMedium: "500" as Font["fontWeight"],
  },
};

// TODO
// https://callstack.github.io/react-native-paper/fonts.html#configuring-fonts-in-themeprovider
const fontConfig = {
  web: {
    regular: {
      fontFamily: "sans-serif",
      fontWeight: "normal",
    },
    medium: {
      fontFamily: "sans-serif-medium",
      fontWeight: "normal",
    },
    light: {
      fontFamily: "sans-serif-light",
      fontWeight: "normal",
    },
    thin: {
      fontFamily: "sans-serif-thin",
      fontWeight: "normal",
    },
  },
  ios: {
    regular: {
      fontFamily: "System",
      fontWeight: "normal",
    },
    medium: {
      fontFamily: "System",
      fontWeight: "normal",
    },
    light: {
      fontFamily: "System",
      fontWeight: "normal",
    },
    thin: {
      fontFamily: "System",
      fontWeight: "normal",
    },
  },
  android: {
    regular: {
      fontFamily: "sans-serif",
      fontWeight: "normal",
    },
    medium: {
      fontFamily: "sans-serif-medium",
      fontWeight: "normal",
    },
    light: {
      fontFamily: "sans-serif-light",
      fontWeight: "normal",
    },
    thin: {
      fontFamily: "sans-serif-thin",
      fontWeight: "normal",
    },
  },
};

const regularType = {
  fontFamily: tokens.typeface.brandRegular,
  letterSpacing: 0,
  fontWeight: tokens.typeface.weightRegular,
};

const mediumType = {
  fontFamily: tokens.typeface.plainMedium,
  letterSpacing: 0.15,
  fontWeight: tokens.typeface.weightMedium,
};

const typescale = {
  displayLarge: {
    ...regularType,
    lineHeight: 64,
    fontSize: 57,
  },
  displayMedium: {
    ...regularType,
    lineHeight: 52,
    fontSize: 45,
  },
  displaySmall: {
    ...regularType,
    lineHeight: 44,
    fontSize: 36,
  },

  headlineLarge: {
    ...regularType,
    lineHeight: 40,
    fontSize: 32,
  },
  headlineMedium: {
    ...regularType,
    lineHeight: 36,
    fontSize: 28,
  },
  headlineSmall: {
    ...regularType,
    lineHeight: 32,
    fontSize: 24,
  },

  titleLarge: {
    ...regularType,
    lineHeight: 28,
    fontSize: 22,
  },
  titleMedium: {
    ...mediumType,
    lineHeight: 24,
    fontSize: 16,
  },
  titleSmall: {
    ...mediumType,
    letterSpacing: 0.1,
    lineHeight: 20,
    fontSize: 14,
  },

  labelLarge: {
    ...mediumType,
    letterSpacing: 0.1,
    lineHeight: 20,
    fontSize: 14,
  },
  labelMedium: {
    ...mediumType,
    letterSpacing: 0.5,
    lineHeight: 16,
    fontSize: 12,
  },
  labelSmall: {
    ...mediumType,
    letterSpacing: 0.5,
    lineHeight: 16,
    fontSize: 11,
  },

  bodyLarge: {
    ...mediumType,
    fontWeight: tokens.typeface.weightRegular,
    fontFamily: tokens.typeface.brandRegular,
    lineHeight: 24,
    fontSize: 16,
  },
  bodyMedium: {
    ...mediumType,
    fontWeight: tokens.typeface.weightRegular,
    fontFamily: tokens.typeface.brandRegular,
    letterSpacing: 0.25,
    lineHeight: 20,
    fontSize: 14,
  },
  bodySmall: {
    ...mediumType,
    fontWeight: tokens.typeface.weightRegular,
    fontFamily: tokens.typeface.brandRegular,
    letterSpacing: 0.4,
    lineHeight: 16,
    fontSize: 12,
  },
};

// Theme is built based on default react-navigation theme + our tokens on top to conform to MD3/RNN
const MDTheme: MD3Theme = {
  dark: false,
  roundness: 12,
  version: 3,
  isV3: true,
  colors: {
    primary: "rgb(0, 106, 101)",
    onPrimary: "rgb(255, 255, 255)",
    primaryContainer: "rgb(112, 247, 238)",
    onPrimaryContainer: "rgb(0, 32, 30)",
    secondary: "rgb(93, 83, 167)",
    onSecondary: "rgb(255, 255, 255)",
    secondaryContainer: "rgb(228, 223, 255)",
    onSecondaryContainer: "rgb(24, 2, 98)",
    tertiary: "rgb(118, 90, 0)",
    onTertiary: "rgb(255, 255, 255)",
    tertiaryContainer: "rgb(255, 223, 150)",
    onTertiaryContainer: "rgb(37, 26, 0)",
    error: "rgb(186, 26, 26)",
    onError: "rgb(255, 255, 255)",
    errorContainer: "rgb(255, 218, 214)",
    onErrorContainer: "rgb(65, 0, 2)",
    background: "rgb(250, 253, 251)",
    onBackground: "rgb(25, 28, 28)",
    surface: "rgb(250, 253, 251)",
    onSurface: "rgb(25, 28, 28)",
    surfaceVariant: "rgb(218, 229, 227)",
    onSurfaceVariant: "rgb(63, 73, 71)",
    outline: "rgb(111, 121, 120)",
    outlineVariant: "rgb(190, 201, 199)",
    shadow: "rgb(0, 0, 0)",
    scrim: "rgb(0, 0, 0)",
    inverseSurface: "rgb(45, 49, 48)",
    inverseOnSurface: "rgb(239, 241, 240)",
    inversePrimary: "rgb(79, 218, 210)",
    elevation: {
      level0: "transparent",
      level1: "rgb(238, 246, 244)",
      level2: "rgb(230, 241, 239)",
      level3: "rgb(223, 237, 235)",
      level4: "rgb(220, 235, 233)",
      level5: "rgb(215, 232, 230)",
    },
    surfaceDisabled: "rgba(25, 28, 28, 0.12)",
    onSurfaceDisabled: "rgba(25, 28, 28, 0.38)",
    backdrop: "rgba(41, 50, 49, 0.4)",
  },
  typescale,
  fonts: configureFonts(fontConfig),
  animation: {
    scale: 1.0,
  },
};

const ReactNavigationTheme: Theme = {
  dark: false,
  colors: {
    primary: MDTheme.colors.primary,
    background: MDTheme.colors.background,
    card: MDTheme.colors.surface,
    text: MDTheme.colors.onSurface,
    border: MDTheme.colors.onSurface,
    notification: MDTheme.colors.error,
  },
};

type ThemeOverride = {
  [K in keyof Theme]?: K extends "colors" ? Partial<Theme[K]> : Theme[K];
};
export function buildTheme(theme?: ThemeOverride) {
  return {
    ...MDTheme,
    ...ReactNavigationTheme,
    ...theme,
    colors: {
      ...MDTheme.colors,
      ...ReactNavigationTheme.colors,
      ...theme?.colors,
    },
  };
}
